import React from "react";
import { api, formatApiError } from "../lib/api";
import { Building2, AlertCircle, ArrowRight, Check, X } from "lucide-react";
import { toast } from "sonner";

function ruleLine(r) {
    if (!r) return null;
    if (r.payment_type === "rev_share") return `${r.rev_share_pct}% rev-share`;
    return `$${r.flat_amount} flat`;
}

function ChangeCard({ change, onDecide }) {
    const [busy, setBusy] = React.useState(false);
    const decide = async (action) => {
        setBusy(true);
        try {
            await api.post(`/creator/payout-changes/${change.rule_id}/${action}`);
            toast.success(action === "accept" ? "Change accepted" : "Change declined");
            onDecide();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            className="card-ecom p-5 border-l-4 border-l-coral"
            data-testid={`payout-change-${change.rule_id}`}
        >
            <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-5 h-5 text-coral flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                    <div className="font-display text-lg font-semibold leading-tight">
                        {change.brand.name} wants to lower your rate
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        Applies to {change.target_label}
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 surface-soft rounded-xl p-3 mb-4">
                <div className="flex-1 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        Now
                    </div>
                    <div className="font-display font-semibold text-slate-900">
                        {ruleLine(change.current)}
                    </div>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
                <div className="flex-1 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">
                        Proposed
                    </div>
                    <div className="font-display font-semibold text-coral">
                        {ruleLine(change.proposed)}
                    </div>
                </div>
            </div>
            <div className="text-xs text-slate-500 mb-4">
                Past payouts are not affected. If you reject, your current rate stays in
                place.
            </div>
            <div className="flex gap-2">
                <button
                    onClick={() => decide("accept")}
                    disabled={busy}
                    className="btn-pill-primary inline-flex items-center text-sm"
                    data-testid={`accept-change-${change.rule_id}`}
                >
                    <Check className="w-4 h-4 mr-1.5" /> Accept new rate
                </button>
                <button
                    onClick={() => decide("reject")}
                    disabled={busy}
                    className="btn-pill-ghost inline-flex items-center text-sm"
                    data-testid={`reject-change-${change.rule_id}`}
                >
                    <X className="w-4 h-4 mr-1.5" /> Keep current rate
                </button>
            </div>
        </div>
    );
}

export default function CreatorBrandsPanel() {
    const [terms, setTerms] = React.useState([]);
    const [changes, setChanges] = React.useState([]);
    const [loading, setLoading] = React.useState(true);

    const load = React.useCallback(async () => {
        try {
            const [t, c] = await Promise.all([
                api.get("/creator/effective-terms"),
                api.get("/creator/payout-changes"),
            ]);
            setTerms(t.data);
            setChanges(c.data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    if (loading) {
        return (
            <div className="card-ecom p-8 text-sm text-slate-500" data-testid="brands-loading">
                Loading…
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid="creator-brands-panel">
            {changes.length > 0 && (
                <div className="space-y-3">
                    <div className="overline text-coral font-semibold">
                        ACTION REQUIRED · {changes.length} pending rate{" "}
                        {changes.length === 1 ? "change" : "changes"}
                    </div>
                    {changes.map((ch) => (
                        <ChangeCard key={ch.rule_id} change={ch} onDecide={load} />
                    ))}
                </div>
            )}

            {terms.length === 0 ? (
                <div className="card-ecom p-8 text-sm text-slate-500" data-testid="no-brands">
                    Brands you connect with via invite will appear here, along with the
                    payout terms they've set for your videos.
                </div>
            ) : (
                <div
                    className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    data-testid="brands-list"
                >
                    {terms.map((t) => (
                        <div
                            key={t.brand.id}
                            className="card-ecom p-5"
                            data-testid={`brand-${t.brand.id}`}
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-coral to-lime flex items-center justify-center text-white font-display font-bold text-xl flex-shrink-0">
                                    {t.brand.name?.[0]?.toUpperCase() || (
                                        <Building2 className="w-5 h-5" />
                                    )}
                                </div>
                                <div className="min-w-0">
                                    <div className="font-display font-semibold truncate">
                                        {t.brand.name}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">
                                        {t.brand.email}
                                    </div>
                                </div>
                            </div>
                            <div className="surface-soft rounded-xl p-3">
                                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">
                                    Your rate
                                </div>
                                <div className="font-display text-xl font-semibold text-slate-900">
                                    {t.rule ? ruleLine(t.rule) : "Not set yet"}
                                </div>
                                {t.source && (
                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                        from {t.source} terms
                                    </div>
                                )}
                            </div>
                            {t.pending_for_me && (
                                <div className="mt-3 text-xs chip chip-coral">
                                    Pending: {ruleLine(t.pending_for_me.proposed)} — see Action required
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
